// TodayWidget — the guardian-facing "where are my kids right now" view.
//
// Reads /me/wards + /schools/today/arrivals via @sms/sdk and renders
// one polished card per ward with today's arrival state. Bilingual via
// the locale prop.
//
// State machine (presentation only; data flow unchanged):
//   loading     -> Spinner panel
//   error       -> Alert + retry button
//   empty wards -> EmptyState
//   loaded      -> Card grid of WardRows

import { useEffect, useState } from "react";

import {
  Alert,
  Button,
  Card,
  EmptyState,
  Spinner,
  Stack,
  tokens,
} from "@sms/design-system";
import type { Locale } from "@sms/i18n";
import { Client, type ArrivalToday, type Ward } from "@sms/sdk";

import { makeT } from "../../i18n";
import { Walkthrough } from "../walkthrough/Walkthrough";
import { isWalkthroughSeen } from "../walkthrough/walkthrough-state";

export interface TodayWidgetProps {
  locale?: Locale;
  client?: Pick<Client, "myWards" | "todayArrivals">;
  /**
   * Optional jump-to-Settings hook the shell wires for the
   * walkthrough's step-4 "Enable push" CTA. Undefined in standalone
   * mode (parent-app run directly via vite dev) — the walkthrough
   * degrades to a plain "Open the app" Done button when missing.
   */
  onJumpToSettings?: () => void;
}

type State =
  | { kind: "loading" }
  | { kind: "error"; reason: string }
  | { kind: "ready"; wards: Ward[]; arrivals: ArrivalToday[] };

const defaultClient = new Client({
  baseURL: typeof window !== "undefined" ? window.location.origin : "",
});

export function TodayWidget({
  locale = "ne",
  client = defaultClient,
  onJumpToSettings,
}: TodayWidgetProps) {
  const t = makeT(locale);
  const [state, setState] = useState<State>({ kind: "loading" });
  const [refreshTick, setRefreshTick] = useState(0);
  // Walkthrough short-circuit. Reads localStorage on first render
  // only — subsequent toggles come from explicit onDone/onEnablePush
  // callbacks, not a re-read. Defaults to false in SSR-ish contexts
  // (no window) so the wards fetch path runs.
  const [showWalkthrough, setShowWalkthrough] = useState(() => {
    if (typeof window === "undefined") return false;
    return !isWalkthroughSeen();
  });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    // Guardians cannot call /schools/today/arrivals (staff-only).
    // myWards is the hard requirement; todayArrivals is best-effort.
    Promise.all([
      client.myWards(),
      client.todayArrivals().catch(() => [] as ArrivalToday[]),
    ])
      .then(([wards, arrivals]) => {
        if (!cancelled) setState({ kind: "ready", wards, arrivals });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "error",
            reason: err instanceof Error ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, refreshTick]);

  if (showWalkthrough) {
    return (
      <Walkthrough
        locale={locale}
        onDone={() => setShowWalkthrough(false)}
        onEnablePush={onJumpToSettings ? () => {
          setShowWalkthrough(false);
          onJumpToSettings();
        } : undefined}
      />
    );
  }

  return (
    <section aria-labelledby="today-heading" style={containerStyle}>
      <header style={headerStyle}>
        <p style={overlineStyle}>{formatToday(locale)}</p>
        <h1 id="today-heading" style={headingStyle}>
          {t("today.title")}
        </h1>
      </header>

      {state.kind === "loading" && (
        <Stack gap={3} align="center" style={{ padding: tokens.space[10] }}>
          <Spinner size={28} label={t("common.loading")} />
          <p style={mutedStyle}>{t("common.loading")}</p>
        </Stack>
      )}

      {state.kind === "error" && (
        <Stack gap={3}>
          <Alert intent="danger" title={t("today.fetch_failed")}>
            {state.reason}
          </Alert>
          <Button
            variant="secondary"
            onClick={() => setRefreshTick((n) => n + 1)}
          >
            {t("actions.retry")}
          </Button>
        </Stack>
      )}

      {state.kind === "ready" && state.wards.length === 0 && (
        <EmptyState
          icon={
            <img
              src="/empty/no-wards.svg"
              alt=""
              width={120}
              height={120}
              style={{ display: "block" }}
            />
          }
          title={t("today.no_wards")}
          description={t("today.no_wards_hint")}
        />
      )}

      {state.kind === "ready" && state.wards.length > 0 && (
        <Stack gap={3}>
          {state.wards.map((ward) => (
            <WardCard
              key={ward.student_id}
              ward={ward}
              arrival={state.arrivals.find(
                (a) => a.student_id === ward.student_id,
              )}
              t={t}
              locale={locale}
            />
          ))}
        </Stack>
      )}
    </section>
  );
}

// ── Ward card ────────────────────────────────────────────────────────

function WardCard({
  ward,
  arrival,
  t,
  locale,
}: {
  ward: Ward;
  arrival: ArrivalToday | undefined;
  t: (key: string, vars?: Record<string, string>) => string;
  locale: Locale;
}) {
  const arrived = arrival && !arrival.voided;
  const arrivedAt = arrived ? formatTime(arrival.arrived_at, locale) : undefined;
  const isLate = arrived && arrival.late;
  const initial = initialOf(ward.student_name);

  return (
    <Card
      variant="elevated"
      padding={4}
      data-testid={`ward-${ward.student_id}`}
      data-arrived={arrived ? "true" : "false"}
    >
      <Stack direction="row" gap={3} align="center">
        <Avatar initial={initial} accent={accentForName(ward.student_name)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={nameStyle}>{ward.student_name}</p>
          <p style={metaStyle}>{ward.class_section}</p>
        </div>
        <StatusPill
          arrived={!!arrived}
          arrivedAt={arrivedAt}
          isLate={!!isLate}
          t={t}
        />
      </Stack>
    </Card>
  );
}

function Avatar({ initial, accent }: { initial: string; accent: string }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 44,
        height: 44,
        flexShrink: 0,
        borderRadius: tokens.radius.pill,
        background: accent,
        color: tokens.color.neutral[0],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        fontWeight: tokens.type.weight.semibold,
        letterSpacing: "-0.01em",
      }}
    >
      {initial}
    </div>
  );
}

function StatusPill({
  arrived,
  arrivedAt,
  isLate,
  t,
}: {
  arrived: boolean;
  arrivedAt: string | undefined;
  isLate: boolean;
  t: (k: string, v?: Record<string, string>) => string;
}) {
  if (!arrived) {
    return (
      <span
        style={pendingPillStyle}
        aria-label={t("today.no_arrival_yet")}
      >
        <span style={pendingDotStyle} />
        <span>{t("today.no_arrival_yet")}</span>
      </span>
    );
  }
  const label = isLate
    ? t("today.late_at", { time: arrivedAt ?? "" })
    : t("today.arrived_at", { time: arrivedAt ?? "" });
  return (
    <span
      style={isLate ? latePillStyle : arrivedPillStyle}
      aria-label={label}
    >
      <CheckIcon />
      <span>{arrivedAt}</span>
    </span>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatTime(rfc3339: string, locale: Locale): string {
  const d = new Date(rfc3339);
  if (Number.isNaN(d.getTime())) return rfc3339;
  return new Intl.DateTimeFormat(locale === "ne" ? "ne-NP" : "en-NP", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kathmandu",
  }).format(d);
}

function formatToday(locale: Locale): string {
  return new Intl.DateTimeFormat(locale === "ne" ? "ne-NP" : "en-NP", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "Asia/Kathmandu",
  }).format(new Date());
}

function initialOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "•";
  // Use the first code-point (handles Devanagari + emoji + Latin).
  return [...trimmed][0]!.toUpperCase();
}

// Deterministic accent colour per name so siblings get visually
// distinct avatars; never picks pure white or pure crimson.
function accentForName(name: string): string {
  const accents = [
    "#0f766e", // teal
    "#7c3aed", // violet
    "#ea580c", // orange
    "#0369a1", // blue
    "#65a30d", // lime
    "#be185d", // pink
    "#475569", // slate
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return accents[Math.abs(hash) % accents.length]!;
}

// ── Styles ───────────────────────────────────────────────────────────

const containerStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  margin: "0 auto",
  padding: tokens.space[4],
};
const headerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: tokens.space[1],
  marginBottom: tokens.space[5],
};
const overlineStyle: React.CSSProperties = {
  fontSize: tokens.type.size.xs,
  fontWeight: tokens.type.weight.semibold,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: tokens.color.semantic.textMuted,
};
const headingStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: tokens.type.weight.bold,
  color: tokens.color.semantic.heading,
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
};
const nameStyle: React.CSSProperties = {
  fontSize: tokens.type.size.base,
  fontWeight: tokens.type.weight.semibold,
  color: tokens.color.semantic.heading,
  letterSpacing: "-0.01em",
  // Truncate long names gracefully on small screens.
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const metaStyle: React.CSSProperties = {
  fontSize: tokens.type.size.sm,
  color: tokens.color.semantic.textMuted,
  marginTop: 2,
};
const mutedStyle: React.CSSProperties = {
  fontSize: tokens.type.size.sm,
  color: tokens.color.semantic.textMuted,
};

const pillBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: tokens.space[1],
  padding: "6px 12px",
  borderRadius: tokens.radius.pill,
  fontSize: tokens.type.size.xs,
  fontWeight: tokens.type.weight.semibold,
  letterSpacing: "0.01em",
  whiteSpace: "nowrap",
  flexShrink: 0,
};
const arrivedPillStyle: React.CSSProperties = {
  ...pillBase,
  background: tokens.color.success[50],
  color: tokens.color.success[700],
};
const latePillStyle: React.CSSProperties = {
  ...pillBase,
  background: tokens.color.warning[50],
  color: tokens.color.warning[700],
};
const pendingPillStyle: React.CSSProperties = {
  ...pillBase,
  background: tokens.color.semantic.surfaceAlt,
  color: tokens.color.semantic.textMuted,
};
const pendingDotStyle: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  background: tokens.color.neutral[400],
};
